import NiceModal from '@ebay/nice-modal-react';
import { ActionIcon, Button, Group, Loader, Paper, Stack, Text, TextInput } from '@mantine/core';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMemo, useState } from 'react';

import {
  useAddContentCategory,
  useEditContentCategory,
  useGetContentCategories,
  useRemoveContentCategory,
} from '../../api';
import { contentTypes } from '../../types';

import { ConfirmModal } from '@/components/Modals/Confirm';

type Props = {
  pageId: string | null;
  materials: contentTypes.Item[];
};

export const CategoriesPanel = ({ pageId, materials }: Props) => {
  const { data, isLoading: isCategoriesLoading } = useGetContentCategories({ pageId });
  const categories = useMemo(() => data?.categories ?? [], [data?.categories]);
  const addCategoryMutation = useAddContentCategory();
  const editCategoryMutation = useEditContentCategory();
  const removeCategoryMutation = useRemoveContentCategory({ pageId: pageId ?? undefined });

  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleAdd = () => {
    const trimmed = newName.trim();
    if (!trimmed || !pageId) return;

    addCategoryMutation.mutate({ pageId, name: trimmed }, { onSuccess: () => setNewName('') });
  };

  const startEdit = (id: string, name: string) => {
    setEditingId(id);
    setEditValue(name);
  };

  const commitEdit = () => {
    if (!editingId) return;

    const trimmed = editValue.trim();
    const current = categories.find((category) => category._id === editingId);

    if (trimmed && current && trimmed !== current.name && pageId) {
      editCategoryMutation.mutate({ id: editingId, name: trimmed, pageId });
    }

    setEditingId(null);
    setEditValue('');
  };

  const handleRemove = (id: string, name: string) => {
    const usageCount = materials.filter((item) => item.category === name).length;
    const title =
      usageCount > 0
        ? `Категория «${name}» используется в ${usageCount} материалах. Удалить?`
        : `Удалить категорию «${name}»?`;

    NiceModal.show(ConfirmModal, {
      title,
      onSuccess: () => removeCategoryMutation.mutate({ id, pageId: pageId ?? '' }),
    });
  };

  if (!pageId) {
    return (
      <Paper mt="md" radius="md" p="md" withBorder={false} shadow="xs">
        <Text size="sm" c="dimmed">
          Выберите страницу, чтобы управлять категориями
        </Text>
      </Paper>
    );
  }

  if (isCategoriesLoading) {
    return (
      <Paper mt="md" radius="md" p="md" withBorder={false} shadow="xs">
        <Group justify="center" py="xl">
          <Loader size="sm" />
        </Group>
      </Paper>
    );
  }

  return (
    <Paper mt="md" radius="md" p="md" withBorder={false} shadow="xs">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Здесь можно добавлять, переименовывать и удалять категории. Они появятся в выпадающих
          списках при загрузке и редактировании материалов.
        </Text>

        <Group align="flex-end" gap="sm">
          <TextInput
            label="Новая категория"
            placeholder="Название"
            value={newName}
            onChange={(event) => setNewName(event.currentTarget.value)}
            onKeyDown={(event) => event.key === 'Enter' && handleAdd()}
            style={{ flex: 1, maxWidth: 320 }}
          />
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleAdd}
            disabled={!newName.trim()}
            loading={addCategoryMutation.isLoading}
          >
            Добавить
          </Button>
        </Group>

        <Stack gap="xs">
          {categories.length === 0 && (
            <Text size="sm" c="dimmed">
              Нет категорий
            </Text>
          )}

          {categories.map((category) => (
            <Group
              key={category._id}
              justify="space-between"
              wrap="nowrap"
              py="xs"
              px="sm"
              style={{
                borderRadius: 8,
                border: '1px solid var(--mantine-color-dark-4)',
                backgroundColor: 'var(--mantine-color-dark-7)',
              }}
            >
              {editingId === category._id ? (
                <TextInput
                  value={editValue}
                  onChange={(event) => setEditValue(event.currentTarget.value)}
                  onBlur={commitEdit}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') commitEdit();
                    if (event.key === 'Escape') {
                      setEditingId(null);
                      setEditValue('');
                    }
                  }}
                  autoFocus
                  size="xs"
                  style={{ flex: 1 }}
                />
              ) : (
                <Text size="sm" fw={500}>
                  {category.name}
                </Text>
              )}

              <Group gap={4} wrap="nowrap">
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  onClick={() => startEdit(category._id, category.name)}
                  aria-label="Редактировать"
                >
                  <IconPencil size={14} />
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  onClick={() => handleRemove(category._id, category.name)}
                  aria-label="Удалить"
                  loading={removeCategoryMutation.isLoading}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Group>
            </Group>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
};
